import { Icon } from "@iconify/vue";
import type { MenuOption } from "naive-ui";
import { h } from "vue";
import { RouterLink } from "vue-router";
import { applicationRouteName } from "../features/application/presentation/routes/applicationRoute";
import { clientCategoryRouteName } from "../features/client-category/presentation/routes/clientCategoryRoute";
import { clientServiceRouteName } from "../features/client-service/presentation/routes/clientServiceRoute";
import { clientRouteName } from "../features/client/presentation/routes/clientRoute";
import { dashboardRouteName } from "../features/dashboard/presentation/routes/dashboardRoute";
import { departmentRouteName } from "../features/department/presentation/routes/departmentRoute";
import { levelRouteName } from "../features/level/presentation/routes/levelRoute";
import { officeRouteName } from "../features/office/presentation/routes/officeRoute";
import { positionRouteName } from "../features/position/presentation/routes/positionRoute";
import { projectRouteName } from "../features/project/presentation/routes/projectRoute";
import { recruitmentHiringRouteName } from "../features/recruitment-hiring/presentation/routes/recruitmentHiringRoute";
import { recruitmentRequestRouteName } from "../features/recruitment-request/presentation/routes/recruitmentRequestRoute";
import { recruitmentScreeningRouteName } from "../features/recruitment-screening/presentation/routes/recruitmentScreeningRoute";
import { recruitmentSettingRouteName } from "../features/recruitment-setting/presentation/routes/recruitmentSettingRoute";
import { sectionRouteName } from "../features/section/presentation/routes/sectionRoute";
import { terminationCategoryRouteName } from "../features/termination-category/presentation/routes/terminationCategoryRoute";
import { terminationRequestRouteName } from "../features/termination-request/presentation/routes/terminationRequestRoute";
import { terminationSettingRouteName } from "../features/termination-setting/presentation/routes/terminationSettingRoute";
import { vendorCategoryRouteName } from "../features/vendor-category/presentation/routes/vendorCategoryRoute";
import { vendorServiceRouteName } from "../features/vendor-service/presentation/routes/vendorServiceRoute";
import { vendorRouteName } from "../features/vendor/presentation/routes/vendorRoute";
import { workScheduleRouteName } from "../features/work-schedule/presentation/routes/workScheduleRoute";
import { employeeRouteName } from "./../features/employee/presentation/routes/employeRoute";

export interface AppMenuOption extends Omit<MenuOption, "children"> {
  permissionKey?: string;
  children?: AppMenuOption[];
}

export interface UserPermission {
  name: string;
  canView: boolean;
  canAdd?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

export const menuUtil = function (t: any): AppMenuOption[] {
  const menuOptions: AppMenuOption[] = [
    {
      key: "dashboard",
      label: () =>
        h(
          RouterLink,
          {
            to: {
              name: dashboardRouteName.index,
            },
          },
          { default: () => t("menu.dashboard") },
        ),
      icon: () =>
        h(Icon, {
          icon: "solar:widget-2-outline",
        }),
    },
    {
      key: "recruitment",
      label: t("menu.recruitment"),
      type: "group",
      children: [
        {
          key: "recruitment_request",
          permissionKey: "recruitment_request",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: recruitmentRequestRouteName.index,
                },
              },
              { default: () => t("menu.request") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:pen-new-round-linear",
            }),
        },
        {
          key: "recruitment_screening",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: recruitmentScreeningRouteName.index,
                },
              },
              { default: () => t("menu.screening") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:diploma-verified-linear",
            }),
        },
        {
          key: "recruitment_hiring",
          permissionKey: "recruitment_hiring",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: recruitmentHiringRouteName.index,
                },
              },
              { default: () => t("menu.hiring") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:card-search-linear",
            }),
        },
        {
          key: "recruitment_setting",
          permissionKey: "recruitment_setting",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: recruitmentSettingRouteName.index,
                },
              },
              { default: () => t("menu.setting") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:settings-minimalistic-linear",
            }),
        },
      ],
    },

    {
      key: "termination",
      label: t("menu.termination"),
      type: "group",
      children: [
        {
          key: "termination_request",
          permissionKey: "termination_request",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: terminationRequestRouteName.index,
                },
              },
              { default: () => t("menu.request") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:pen-new-round-linear",
            }),
        },
        {
          key: "termination_category",
          permissionKey: "termination_category",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: terminationCategoryRouteName.index,
                },
              },
              { default: () => t("menu.category") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:bill-outline",
            }),
        },
        {
          key: "termination_setting",
          permissionKey: "termination_setting",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: terminationSettingRouteName.index,
                },
              },
              { default: () => t("menu.setting") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:settings-minimalistic-linear",
            }),
        },
        // {
        //   key: "termination_reason",
        //   permissionKey: "termination_reason",
        //   label: () =>
        //     h(
        //       RouterLink,
        //       {
        //         to: {
        //           name: terminationReasonRouteName.index,
        //         },
        //       },
        //       { default: () => t("reason") },
        //     ),
        //   icon: () =>
        //     h(Icon, {
        //       icon: "solar:question-square-outline",
        //     }),
        // },
      ],
    },
    {
      key: "company",
      label: t("menu.company"),
      type: "group",
      children: [
        {
          key: "office",
          permissionKey: "office",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: officeRouteName.index,
                },
              },
              { default: () => t("menu.office") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:buildings-outline",
            }),
        },
        {
          key: "employee",
          permissionKey: "employee",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: employeeRouteName.index,
                },
              },
              { default: () => t("menu.employee") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:user-outline",
            }),
        },
        {
          key: "structure",
          label: t("menu.structure"),
          type: "group",
          children: [
            {
              key: "department",
              permissionKey: "department",
              label: () =>
                h(
                  RouterLink,
                  {
                    to: {
                      name: departmentRouteName.index,
                    },
                  },
                  { default: () => t("menu.department") },
                ),
              icon: () =>
                h(Icon, {
                  icon: "solar:suitcase-tag-outline",
                }),
            },
            {
              key: "section",
              permissionKey: "section",
              label: () =>
                h(
                  RouterLink,
                  {
                    to: {
                      name: sectionRouteName.index,
                    },
                  },
                  { default: () => t("menu.section") },
                ),
              icon: () =>
                h(Icon, {
                  icon: "solar:suitcase-tag-outline",
                }),
            },
            {
              key: "level",
              permissionKey: "level",
              label: () =>
                h(
                  RouterLink,
                  {
                    to: {
                      name: levelRouteName.index,
                    },
                  },
                  { default: () => t("menu.level") },
                ),
              icon: () =>
                h(Icon, {
                  icon: "solar:suitcase-tag-outline",
                }),
            },
            {
              key: "position",
              permissionKey: "position",
              label: () =>
                h(
                  RouterLink,
                  {
                    to: {
                      name: positionRouteName.index,
                    },
                  },
                  { default: () => t("menu.position") },
                ),
              icon: () =>
                h(Icon, {
                  icon: "solar:suitcase-tag-outline",
                }),
            },
          ],
        },
      ],
    },
    {
      key: "client",
      label: t("menu.client"),
      type: "group",
      children: [
        {
          key: "client_entity",
          permissionKey: "client_entity",
          label: () =>
            h(
              RouterLink,
              {
                to: { name: clientRouteName.index },
              },
              { default: () => t("menu.entity") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:users-group-two-rounded-outline",
            }),
        },
        {
          key: "client_service",
          permissionKey: "client_service",
          label: () =>
            h(
              RouterLink,
              {
                to: { name: clientServiceRouteName.index },
              },
              { default: () => t("menu.service") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:settings-outline",
            }),
        },
        {
          key: "client_category",
          permissionKey: "client_category",
          label: () =>
            h(
              RouterLink,
              {
                to: { name: clientCategoryRouteName.index },
              },
              { default: () => t("menu.category") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:bill-outline",
            }),
        },
        {
          key: "client_project",
          permissionKey: "client_project",
          label: () =>
            h(
              RouterLink,
              {
                to: { name: projectRouteName.index },
              },
              { default: () => t("menu.project") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:notebook-outline",
            }),
        },
      ],
    },
    {
      key: "vendor",
      label: t("menu.vendor"),
      type: "group",
      children: [
        {
          key: "vendor_entity",
          permissionKey: "vendor_entity",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: vendorRouteName.index,
                },
              },
              { default: () => t("menu.entity") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:users-group-two-rounded-outline",
            }),
        },
        {
          key: "vendor_service",
          permissionKey: "vendor_service",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: vendorServiceRouteName.index,
                },
              },
              { default: () => t("menu.service") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:settings-outline",
            }),
        },
        {
          key: "vendor_category",
          permissionKey: "vendor_category",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: vendorCategoryRouteName.index,
                },
              },
              { default: () => t("menu.category") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:bill-outline",
            }),
        },
      ],
    },
    {
      key: "attendance",
      label: t("menu.attendance"),
      type: "group",
      children: [
        {
          key: "work_schedule",
          permissionKey: "work_schedule",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: workScheduleRouteName.index,
                },
              },
              { default: () => t("menu.workSchedule") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:calendar-outline",
            }),
        },
      ],
    },
    {
      key: "access",
      label: t("menu.access"),
      type: "group",
      children: [
        {
          key: "application",
          permissionKey: "application",
          label: () =>
            h(
              RouterLink,
              {
                to: {
                  name: applicationRouteName.index,
                },
              },
              { default: () => t("menu.application") },
            ),
          icon: () =>
            h(Icon, {
              icon: "solar:laptop-minimalistic-outline",
            }),
        },
      ],
    },
  ];

  return menuOptions;
};

/**
 * Recursively drops any menu item whose permissionKey the user lacks
 * view access to. A permission must exist AND have canView: true.
 * - Leaf items without a permissionKey (e.g. recruitment_screening) are always kept.
 * - Group items are kept only if at least one child survives filtering.
 */
export function filterMenuByPermission(options: AppMenuOption[], permissions: UserPermission[]): AppMenuOption[] {
  const canView = (key: string) => permissions.some((p) => p.name === key && p.canView);

  return options.reduce<AppMenuOption[]>((acc, item) => {
    if (item.permissionKey && !canView(item.permissionKey)) {
      return acc;
    }

    if (item.children) {
      const children = filterMenuByPermission(item.children, permissions);
      if (children.length === 0) return acc;
      acc.push({ ...item, children });
      return acc;
    }

    acc.push(item);
    return acc;
  }, []);
}
